/**
 * Identidade de quem está logado — nome, papel e escola numa leitura só.
 *
 * Nasceu dentro do MenuDaConta e virou hook quando o /perfil precisou da
 * MESMA leitura: duas cópias divergem na primeira mudança (foi assim que o
 * material de adesivo quase divergiu antes do `adesivo.tsx`).
 *
 * O nome vem da tabela `profiles` (RLS: cada um lê o próprio), com o
 * user_metadata como reserva — `profiles` é o que o /perfil edita, então ler
 * do metadata deixaria o cabeçalho mentindo depois da primeira edição.
 *
 * A escola só resolve para staff: a policy de SELECT de `schools` começa em
 * professor. Para o aluno, `escolaNome` fica null e as telas mostram só o
 * papel — não é erro, é o alcance da RLS.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { usePapel, type Papel } from './usePapel';

/** Como cada papel se apresenta. `admin` é sinônimo legado de superadmin. */
export const ROTULO_DO_PAPEL: Record<Papel, string> = {
  superadmin: 'Superadmin',
  admin: 'Superadmin',
  diretor: 'Diretor',
  coordenador: 'Coordenador',
  professor: 'Professor',
  student: 'Aluno',
};

export function iniciaisDe(nome: string | null | undefined): string {
  return (
    nome
      ?.split(' ')
      .map((n) => n[0])
      /* Só letra/número vira inicial: "Matheus (professor)" é "M", não "M(" —
         o sufixo entre parênteses é anotação de papel, não sobrenome. */
      .filter((ch) => ch && /\p{L}|\p{N}/u.test(ch))
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?'
  );
}

export interface Identidade {
  uid: string | undefined;
  email: string | undefined;
  /** `profiles.full_name`, com o metadata do signup como reserva. */
  nome: string | null;
  avatarUrl: string | null;
  papel: Papel;
  /** Rótulo apresentável do papel — null enquanto o papel carrega. */
  rotulo: string | null;
  /** Nome da escola do primeiro vínculo; null para aluno e superadmin. */
  escolaNome: string | null;
  carregando: boolean;
  /** Relê o `profiles` — pro /perfil refletir o que acabou de salvar. */
  recarregar: () => void;
}

export function useIdentidade(): Identidade {
  const { user, isLoading: carregandoSessao } = useAuth();
  const { papel, escolas, carregando: carregandoPapel } = usePapel();

  const [nome, setNome] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [escolaNome, setEscolaNome] = useState<string | null>(null);
  const [carregandoPerfil, setCarregandoPerfil] = useState(true);
  const [versao, setVersao] = useState(0);

  /* ⚠️ Depender de `user?.id` e `escolas[0]`, nunca dos objetos: `useAuth`
     monta um `user` novo a cada render e `usePapel` idem com o array
     `escolas` — como dependência eles re-executam o efeito pra sempre
     (gotcha de 05/08, travou o gate no spinner). */
  const uid = user?.id;
  const escolaId = escolas[0];

  useEffect(() => {
    if (!uid) { setCarregandoPerfil(false); return; }
    let vivo = true;
    setCarregandoPerfil(true);
    supabase
      .from('profiles')
      .select('full_name, avatar_url')
      .eq('user_id', uid)
      .maybeSingle()
      .then(({ data }) => {
        if (!vivo) return;
        setNome(data?.full_name ?? null);
        setAvatarUrl(data?.avatar_url ?? null);
        setCarregandoPerfil(false);
      });
    return () => { vivo = false; };
  }, [uid, versao]);

  useEffect(() => {
    if (!escolaId) { setEscolaNome(null); return; }
    let vivo = true;
    supabase
      .from('schools')
      .select('name')
      .eq('id', escolaId)
      .maybeSingle()
      .then(({ data }) => { if (vivo) setEscolaNome(data?.name ?? null); });
    return () => { vivo = false; };
  }, [escolaId]);

  return {
    uid,
    email: user?.email,
    nome: nome ?? user?.fullName ?? null,
    avatarUrl,
    papel,
    rotulo: carregandoPapel ? null : ROTULO_DO_PAPEL[papel],
    escolaNome,
    carregando: carregandoSessao || carregandoPapel || carregandoPerfil,
    recarregar: () => setVersao((v) => v + 1),
  };
}
